import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ExternalSyncStatus, PageType, RelationshipType } from '@common/entities';
import { QueryFunctionContext, useQuery, UseQueryResult } from '@tanstack/react-query';
import { EntrataSession } from 'auth/EntrataSession';
import { setApplicationSettings } from 'state/slices/applicationSettingsSlice';
import { setApplicationData } from 'state/slices/applicationSlice';
import { setIsLeadProfile } from 'state/slices/authSlice';
import { setIsApplicationLocked } from 'state/slices/fieldSettingsSlice';
import { updateFormData } from 'state/slices/formDataSlice';
import { setApplicantsData, updateApplicantResponses } from 'state/slices/leadProfileSlice';
import {
  replaceExternalSyncErrorsForTab,
  setActiveStep,
  setCurrentTabIndex,
  setLeadProfileResponseId,
  updateStepStatus,
} from 'state/slices/navigationSlice';
import {
  updateAddon,
  updateSelectedSpecials,
  updateUnitSelection,
} from 'state/slices/unitSelectionSlice';
import { setPermission } from 'state/slices/userPermissionSlice';
import { RootState } from 'state/store';
import { useSelector } from 'state/storeHooks';

import { useSetFloorPlanPageDataInStore } from 'hooks/floor-plans';
import { postLeadProfileVerifySession } from 'services';
import { AddonOption } from 'types/addon';
import { getTodaysDate } from 'utils/date';
import { filterApplicationPages } from 'utils/filterApplicationPages';
import { prepareApplicantResponseData } from 'utils/form';
import { getDisplayableExternalSyncErrors } from 'utils/getExternalSyncErrors';
import { sortApplicants } from 'utils/leadProfileUtils';

import { useGetPropertySettings } from '../useGetPropertySettings';
import { useInitializeSpecials } from '../useInitializeSpecials';

type SessionQueryKey = [
  string,
  {
    orgId: number;
    propertyId: number;
    token: string;
    userId: number;
    leaseId?: number;
    entrataApplicationId?: number;
  },
];

interface PreparedResponseData {
  addOns?: AddonOption[];
  [key: string]: any;
}

const fetchSessionData = async ({ queryKey }: QueryFunctionContext<SessionQueryKey>) => {
  const [, { orgId, propertyId, token, userId, leaseId, entrataApplicationId }] = queryKey;

  const response = await postLeadProfileVerifySession({
    orgId,
    propertyId,
    token,
    userId,
    leaseId,
    entrataApplicationId,
  });

  return response;
};

type Props = {
  orgId: number;
  propertyId: number;
  token: string;
  userId: number;
  leaseId?: number;
  entrataApplicationId?: number;
  isLeadProfile: boolean;
  isPropertyLocaleReady: boolean;
};

type LeadProfileData = any;

export const useLeadProfileData = ({
  orgId,
  propertyId,
  token,
  userId,
  leaseId,
  entrataApplicationId,
  isLeadProfile,
  isPropertyLocaleReady,
}: Props): UseQueryResult<LeadProfileData> & { isProcessing: boolean; isInitialized: boolean } => {
  const dispatch = useDispatch();
  const [isProcessing, setIsProcessing] = useState(false);
  const [initializedSessionData, setInitializedSessionData] = useState<LeadProfileData>();
  const hasInitialized = useRef(false);

  const storedLeadProfileResponseId = useSelector(
    (state: RootState) => state.navigation.leadProfileResponseId,
  );

  const initializeFloorPlanPageData = useSetFloorPlanPageDataInStore();
  const initializePropertySettings = useGetPropertySettings();
  const initializeSpecials = useInitializeSpecials();

  const queryResult = useQuery({
    queryKey: [
      'verify-session-data',
      { orgId, propertyId, token, userId, leaseId, entrataApplicationId },
    ],
    queryFn: fetchSessionData,
    enabled: !!orgId && !!propertyId && !!token && !!userId && isPropertyLocaleReady,
  });

  const setUserPermission = (responseData: any) => {
    if (!responseData?.permissions) return;

    const permissions = { ...responseData.permissions };
    dispatch(setPermission(permissions));
    const { editApplicationDetails = false } = permissions;
    if (!editApplicationDetails) {
      dispatch(setIsApplicationLocked(!editApplicationDetails));
    }
  };

  const handleDataSuccess = async (data: any) => {
    try {
      setIsProcessing(true);
      dispatch(setApplicantsData(data));
      dispatch(updateFormData(data.applicationForm));
      dispatch(setApplicationSettings(data?.settings ?? {}));
      dispatch(
        setApplicationData({
          applicant: data.primaryApplicant,
          form: data.applicationForm,
          activeResponseId: data.primaryResponseId,
        }),
      );
      dispatch(setIsLeadProfile(true));
      EntrataSession.setSessionOrigin('lead_profile');
      EntrataSession.setApplicant(data.primaryApplicant);
      setUserPermission(data);

      // Sort to match the canonical order stored in Redux so tabIndex-keyed state
      // (stepStatus, activeStepByTab, currentTabIndex) lines up with the rendered tabs.
      const sortedApplicants = sortApplicants(data.applicants);

      let activeTabIndex = 0;
      if (storedLeadProfileResponseId) {
        const matchingIndex = sortedApplicants.findIndex(
          (a: any) => a.responseId === storedLeadProfileResponseId,
        );
        if (matchingIndex !== -1) {
          activeTabIndex = matchingIndex;
          // Only on initial load; on re-fetches LPController already holds the correct tab index.
          if (!hasInitialized.current) {
            dispatch(setCurrentTabIndex(matchingIndex));
          }
        }
      }

      await (sortedApplicants as any[]).reduce(
        (prevPromise: Promise<void>, applicant, tabIndex) =>
          prevPromise.then(async () => {
            const { responseDetails, leaseId: applicantLeaseId, relationshipType } = applicant;

            const filteredPages = filterApplicationPages(
              data.applicationForm,
              relationshipType,
              undefined,
              isLeadProfile,
            );

            const floorPlanPage = filteredPages.find(
              (page: any) => page.type === PageType.FloorPlans,
            );

            const floorPlanResponsePage = responseDetails?.pages?.find(
              (page: any) => page.pageId === floorPlanPage?.id,
            );

            if (floorPlanPage && floorPlanResponsePage) {
              const floorPlanResponse = floorPlanResponsePage.responses.find(
                (response: any) => response.floorPlanData,
              );
              const waitlistResponse = floorPlanResponsePage.responses.find(
                (response: any) => response.waitlistSelections,
              );

              if (
                !floorPlanResponse &&
                (!waitlistResponse || waitlistResponse.waitlistSelections.floorplanIds.length === 0)
              ) {
                // TODO: Temp code
                dispatch(updateUnitSelection({ moveInDate: getTodaysDate() }));
              } else {
                const floorPlanData = floorPlanResponse?.floorPlanData;
                const {
                  floorplanId,
                  unitSpaceId,
                  termMonth,
                  moveInDate,
                  spaceConfigurationId,
                  leaseTermId,
                  leaseStartWindowId,
                  preferredAmenityIds,
                  isStudentLaunch,
                } = floorPlanData || {};

                let moveInDateToUse = '';
                if (!moveInDate) {
                  moveInDateToUse = getTodaysDate();
                } else {
                  const todaysDate = getTodaysDate();
                  const parsedTodaysDate = Date.parse(todaysDate);
                  const parsedMoveInDate = Date.parse(moveInDate);
                  moveInDateToUse = parsedMoveInDate > parsedTodaysDate ? moveInDate : todaysDate;
                }

                const errorCount = await initializeFloorPlanPageData(
                  moveInDateToUse,
                  floorplanId,
                  unitSpaceId,
                  termMonth,
                  data.primaryApplicant.propertyId,
                  applicantLeaseId,
                  waitlistResponse?.waitlistSelections,
                  spaceConfigurationId,
                  leaseTermId,
                  leaseStartWindowId, // NEW parameter
                  preferredAmenityIds,
                  isStudentLaunch,
                );

                if (!floorPlanPage?.id || !floorPlanResponsePage) return;

                dispatch(
                  updateStepStatus({
                    tabIndex,
                    step: floorPlanPage.id,
                    status: { errorCount },
                  }),
                );
              }
            }

            const prepData = prepareApplicantResponseData(responseDetails);

            dispatch(
              updateApplicantResponses({
                responseId: applicant.responseId,
                data: prepData,
              }),
            );

            if (!hasInitialized.current && tabIndex === activeTabIndex) {
              dispatch(setLeadProfileResponseId(applicant.responseId));
              dispatch(setCurrentTabIndex(tabIndex));
              hasInitialized.current = true;
            }

            const updatedStepIds = new Set<string>();
            const externalSyncErrorsByPageId = (responseDetails?.pages ?? []).reduce(
              (errors: Record<string, { operation: string; message: string }[]>, page: any) => {
                const externalSyncErrors = getDisplayableExternalSyncErrors(
                  page.externalSync,
                  page.errorCount,
                );
                if (externalSyncErrors.length > 0) {
                  return {
                    ...errors,
                    [page.pageId]: externalSyncErrors,
                  };
                }
                return errors;
              },
              {},
            );
            dispatch(
              replaceExternalSyncErrorsForTab({
                tabIndex,
                errorsByPageId: externalSyncErrorsByPageId,
              }),
            );
            filteredPages.forEach((page: any) => {
              const pageId = page.id;
              if (updatedStepIds.has(pageId)) return;
              updatedStepIds.add(pageId);

              const foundPage = responseDetails?.pages?.find((p: any) => p.pageId === pageId);
              const hasExternalSyncError =
                foundPage?.externalSync !== undefined &&
                foundPage.externalSync.status !== ExternalSyncStatus.Succeeded;
              const errorCount = hasExternalSyncError
                ? Math.max(foundPage?.errorCount ?? 0, 1)
                : (foundPage?.errorCount ?? null);

              dispatch(
                updateStepStatus({
                  tabIndex,
                  step: pageId,
                  status: { errorCount },
                }),
              );
            });

            const activePageId =
              responseDetails?.pages?.find((p: any) => p.isCurrentPage)?.pageId ??
              filteredPages[0]?.id;

            if (activePageId) {
              dispatch(setActiveStep({ tabIndex, step: activePageId }));
            }
          }),
        Promise.resolve(),
      );

      const primaryApplicant = data.applicants.find(
        (applicant: any) => applicant.relationshipType === RelationshipType.Primary,
      );

      if (primaryApplicant) {
        const preparedData: PreparedResponseData = prepareApplicantResponseData(
          primaryApplicant.responseDetails,
        );

        if (preparedData?.addOns) {
          dispatch(updateAddon(preparedData.addOns));
        }
        if (preparedData?.selectedSpecials) {
          dispatch(updateSelectedSpecials(preparedData.selectedSpecials));
        }
      }

      await initializePropertySettings(propertyId);
      await initializeSpecials();
      setInitializedSessionData(data);
    } finally {
      setIsProcessing(false);
    }
  };

  const { data, isSuccess } = queryResult;

  useEffect(() => {
    if (data && isSuccess) {
      handleDataSuccess(data.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, data, orgId, propertyId, token, userId, leaseId]);

  return {
    ...queryResult,
    isProcessing,
    isInitialized:
      isSuccess &&
      initializedSessionData !== undefined &&
      initializedSessionData === data?.data &&
      !isProcessing,
  };
};
